import { Component, Input } from '@angular/core';
import { LocationStrategy } from '@angular/common';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { BalanceConfirmation } from 'src/app/models/BroadcastFunds';

/**
 * O que o modal diz que acontece na hora do envio. É o `on_shortfall` do backend, menos o "~0":
 * sem saldo para nem uma unidade, "sai parcial (~0 mensagens)" seria falso — nada sai.
 * `partial_cancel` com 0 vira `cancel_all`; `partial_wait` com 0 vira `wait_all` (ligação do
 * pacote: nada sai agora e tudo fica aguardando recarga).
 */
export type ShortfallOutcome = 'partial_cancel' | 'partial_wait' | 'cancel_all' | 'wait_all';

const quantityFormat = new Intl.NumberFormat('pt-BR');

/**
 * Confirmação de agendar sem saldo para o envio inteiro (T.10, 17tm466kqu7).
 *
 * O backend respondeu 422 `insufficient_balance_confirmation` e não gravou nada. O modal diz
 * quanto custa, quanto há e o que acontece na hora do envio (sai parcial, ou é cancelado quando
 * o envio não sai parcial), e só fecha com `true` pelo botão "Agendar mesmo assim" — ESC e
 * "Cancelar" dispensam. "Recarregar" abre a recarga em OUTRA aba e deixa o modal aberto: o
 * formulário preenchido continua aqui. Quem abre é o `PartialSendConfirmService`.
 */
@Component({
  selector: 'app-partial-send-confirm-modal',
  templateUrl: './partial-send-confirm-modal.component.html',
  styleUrls: ['./partial-send-confirm-modal.component.scss'],
})
export class PartialSendConfirmModalComponent {

  @Input() confirmation: BalanceConfirmation;

  constructor(
    public activeModal: NgbActiveModal,
    private router: Router,
    private locationStrategy: LocationStrategy,
  ) {}

  get partialQuantity(): string {
    return quantityFormat.format(this.confirmation.estimated_partial_quantity);
  }

  get outcome(): ShortfallOutcome {
    const { on_shortfall: onShortfall, estimated_partial_quantity: quantity } = this.confirmation;

    if (quantity > 0 || onShortfall === 'cancel_all') {
      return onShortfall;
    }

    return onShortfall === 'partial_wait' ? 'wait_all' : 'cancel_all';
  }

  /** "saldo" na carteira (R$), "créditos" no pacote. */
  get fundsLabel(): string {
    return this.confirmation.billing === 'wallet' ? 'saldo' : 'créditos';
  }

  get unitLabel(): string {
    return this.confirmation.product === 'call' ? 'ligações' : 'mensagens';
  }

  confirm(): void {
    this.activeModal.close(true);
  }

  cancel(): void {
    this.activeModal.dismiss('CANCEL');
  }

  /**
   * A recarga certa para a cobrança, para abrir em outra aba: carteira abre a recarga da Minha
   * Carteira; pacote vai para a compra de créditos do produto (como o `call4u-audio-create` faz pelo
   * `callBilledByWallet`). `prepareExternalUrl` põe o `#` do roteamento por hash.
   */
  get rechargeHref(): string {
    const tree = this.confirmation.billing === 'wallet'
      ? this.router.createUrlTree(['/wallet'], { queryParams: { recarga: 1 } })
      : this.router.createUrlTree(['/recharges/new'], { queryParams: { target: this.confirmation.product } });

    return this.locationStrategy.prepareExternalUrl(this.router.serializeUrl(tree));
  }
}
